import { useForm } from "react-hook-form";
import { FormControl, MenuItem, Select, Stack } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import * as FormDialog from "../shared/FormDialog";
import { Flag } from "../shared/Flag";
import { LanguageSchema, updateTrustUserInfoMutationOptions, type Language, type TrustUserInfo } from "./trustAPI";

const LANGUAGE_FLAGS: Record<Language, string> = { en: "GB", sv: "SE", no: "NO" };

type TrustUserInfoDialogProps = {
  userInfo: TrustUserInfo;
  onSaved?: () => void;
  /** If not provided, the dialog will not be dismissible */
  onCancel?: () => void;
};

type LanguageOptionProps = {
  language: Language;
};

const LanguageOption = ({ language }: LanguageOptionProps) => {
  const { t } = useTranslation();

  return (
    <Stack component="span" direction="row" sx={{ alignItems: "center", gap: 1 }}>
      <Flag countryCode={LANGUAGE_FLAGS[language]} />
      <span>{t(`Trust.UserInfoDialog.Language.${language}`)}</span>
    </Stack>
  );
};

export const TrustUserInfoDialog = ({ userInfo, onSaved, onCancel }: TrustUserInfoDialogProps) => {
  const { t } = useTranslation();
  const updateTrustUserInfoMutation = useMutation(updateTrustUserInfoMutationOptions());

  const { control, handleSubmit, setFocus } = useForm<TrustUserInfo>({
    defaultValues: userInfo,
  });

  FormDialog.useAutoFocusField(setFocus, "firstName");

  return (
    <FormDialog.Dialog isOpen maxWidth="600px">
      <FormDialog.Form
        onSubmit={handleSubmit(values => updateTrustUserInfoMutation.mutate(values, { onSuccess: onSaved }))}
      >
        <FormDialog.Title>{t("Trust.UserInfoDialog.Title")}</FormDialog.Title>
        <FormDialog.Content>
          <FormDialog.FormError isError={updateTrustUserInfoMutation.isError} />
          <FormDialog.Fields>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormDialog.TextField
                control={control}
                name="firstName"
                rules={{ required: t("Input.Validation.Required") }}
                label={t("Trust.UserInfoDialog.FirstNameLabel")}
              />
              <FormDialog.TextField
                control={control}
                name="lastName"
                rules={{ required: t("Input.Validation.Required") }}
                label={t("Trust.UserInfoDialog.LastNameLabel")}
              />
            </Stack>

            <FormDialog.TextField
              control={control}
              name="jobTitle"
              rules={{ required: t("Input.Validation.Required") }}
              label={t("Trust.UserInfoDialog.JobTitleLabel")}
            />

            <FormDialog.ControlledField
              control={control}
              name="language"
              rules={{ required: true }}
              label={t("Trust.UserInfoDialog.LanguageLabel")}
            >
              {({ field: { ref, ...field }, fieldState, id }) => (
                <FormControl fullWidth size="small" error={Boolean(fieldState.error)}>
                  <Select<Language>
                    {...field}
                    id={id}
                    value={field.value}
                    inputRef={ref}
                    onChange={e => {
                      if (LanguageSchema.safeParse(e.target.value).success) field.onChange(e.target.value);
                    }}
                    renderValue={value => <LanguageOption language={value as Language} />}
                  >
                    {LanguageSchema.options.map(value => (
                      <MenuItem key={value} value={value}>
                        <LanguageOption language={value} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </FormDialog.ControlledField>
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          {onCancel ? (
            <FormDialog.CancelButton
              onClick={() => {
                if (updateTrustUserInfoMutation.isPending) return;
                onCancel();
              }}
            />
          ) : null}
          <FormDialog.SubmitButton isSubmitting={updateTrustUserInfoMutation.isPending}>
            {t("Trust.UserInfoDialog.Save")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
